/**
 * Knowledge › Company skills, the parts without React (screens/CompanySkills.tsx): what the drawer checks before it
 * sends (the limits GET /v1/admin/company-skills reports, gyro-svc userSkills.ts validateSkillInput's rules), what a
 * refusal says in plain words, and which form field a refusal belongs to.
 */
import { ApiError, type CompanySkillLimits } from './api';
import { errorText } from './errors';

export type SkillFormField = 'name' | 'description' | 'aliases' | 'body' | 'authorName';

/** The publish/edit form as typed: aliases as one comma-separated line. */
export interface SkillForm {
  name: string;
  description: string;
  aliases: string;
  body: string;
  authorName: string;
}

export const EMPTY_SKILL_FORM: SkillForm = { name: '', description: '', aliases: '', body: '', authorName: '' };

/** The longest author name the service takes (adminSchema.ts SKILL_AUTHOR_NAME_MAX). */
export const AUTHOR_NAME_MAX = 120;
const NAME_CHARS = /^[A-Za-z0-9 ._-]+$/;
const NOT_PLAIN_TEXT = /[\p{Cc}\p{Cf}\u2028\u2029]/u;
const OBJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The aliases typed, split on commas, each trimmed, none empty, none twice. */
export function aliasesOf(text: string): string[] {
  return [...new Set(text.split(',').map((alias) => alias.trim()).filter(Boolean))];
}

/** An Entra object id (a GUID), in any case. */
export function isObjectId(text: string): boolean {
  return OBJECT_ID.test(text.trim());
}

/**
 * What is wrong with the form before it is sent, by field, as the service would refuse it: the name's characters and
 * length, the description's length, the number and length of the aliases, the steps' length, and a plain one-line
 * author name. Empty when it can be sent.
 */
export function formProblems(form: SkillForm, limits: CompanySkillLimits): Partial<Record<SkillFormField, string>> {
  const problems: Partial<Record<SkillFormField, string>> = {};
  const name = form.name.replace(/\s+/g, ' ').trim();
  if (!name) problems.name = 'Give the skill a name.';
  else if (name.length > limits.name) problems.name = `At most ${limits.name} characters (this is ${name.length}).`;
  else if (!NAME_CHARS.test(name)) problems.name = 'Use letters, digits, spaces, dots, underscores or hyphens only.';
  const description = form.description.replace(/\s+/g, ' ').trim();
  if (description.length > limits.description) problems.description = `At most ${limits.description} characters (this is ${description.length}).`;
  const aliases = [...new Set(aliasesOf(form.aliases).map((alias) => alias.toLowerCase().replace(/\s+/g, ' ')))];
  const aliasMin = limits.aliasMin ?? 3;
  if (aliases.length > limits.aliases) problems.aliases = `At most ${limits.aliases} aliases (this is ${aliases.length}).`;
  else if (aliases.some((alias) => alias.length > limits.alias)) problems.aliases = `Each alias is at most ${limits.alias} characters.`;
  else if (aliases.some((alias) => alias.length < aliasMin)) problems.aliases = `Each alias has at least ${aliasMin} characters.`;
  const body = form.body.replace(/\r\n?/g, '\n').trim();
  if (!body) problems.body = 'Write the steps.';
  else if (body.length > limits.body) problems.body = `At most ${limits.body.toLocaleString('en-US')} characters (this is ${body.length.toLocaleString('en-US')}).`;
  const author = form.authorName.trim();
  if (author.length > AUTHOR_NAME_MAX) problems.authorName = `At most ${AUTHOR_NAME_MAX} characters.`;
  else if (NOT_PLAIN_TEXT.test(author)) problems.authorName = 'One line of plain text: no line breaks or hidden characters.';
  return problems;
}

/** The service's own sentence on a refusal, when it sent one. */
function serviceSentence(error: ApiError): string | undefined {
  const message = error.body?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
}

/**
 * What a refused change says, in plain words: the status decides for sign-in, permission, a skill that is gone and an
 * outage (a service sentence is used where it names something more exact, such as a person not found or a limit).
 */
export function skillRefusalText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const code = typeof error.body?.error === 'string' ? error.body.error : undefined;
    if (error.status === 401) return 'Your sign-in has expired. Sign in again.';
    if (error.status === 403) return 'Only Gyro admins can change company skills.';
    if (error.status === 404) return code === 'person_not_found' ? serviceSentence(error) ?? 'Gyro knows no such person.' : 'That skill is no longer there.';
    if (error.status === 503) return code === 'people_unavailable' ? serviceSentence(error) ?? "The people Gyro knows can't be read right now; try again in a minute." : "Saved skills can't be reached right now; try again in a minute.";
    if (error.status === 415) return 'The service refused how the change was sent. Reload the page and try again.';
    return serviceSentence(error) ?? fallback;
  }
  return errorText(error, fallback);
}

/** What a failed read of a person's skills says: no such person, an address on two people, or an outage. */
export function personRefusalText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return "Only IT admins and auditors can read a person's skills.";
    if (error.status === 404) return serviceSentence(error) ?? 'Gyro knows no desktop sign-in for this address. Use their Entra object id instead.';
    if (error.status === 409) return serviceSentence(error) ?? 'This address belongs to more than one person; use their object id.';
  }
  return skillRefusalText(error, 'Their skills could not be read right now.');
}

/** The form field a service refusal names (413 skill_limit's limit, 400's field), or null when it names none. */
export function refusalField(error: unknown): SkillFormField | null {
  if (!(error instanceof ApiError)) return null;
  const named = typeof error.body?.limit === 'string' ? error.body.limit : typeof error.body?.field === 'string' ? error.body.field : '';
  const head = named.split('.')[0];
  if (head === 'name' || head === 'description' || head === 'body' || head === 'authorName') return head;
  if (head === 'aliases' || head === 'alias') return 'aliases';
  return null;
}
